/**
 * pluginTranscriptions — KidRenderingPlugin for media/applog transcriptions.
 *
 * Auto-discovers media entities (entities with media/cid applogs) and injects
 * them as virtual roots into the note3 tree. When TreeItem renders such an
 * entity, matchesEntity kicks in and the Root component shows the transcript
 * segments linked via segment/media.
 *
 * No block/type or block/content applogs are written — the plugin works
 * entirely with virtual entities derived from applog data.
 */

import type { EntityID, Thread } from '@wovin/core'
import type { KidRenderingPlugin } from '../../types'
import { query } from '@wovin/core'
import { Iconify } from '../../../components/mini-components'
import { TranscriptionsRoot } from './TranscriptionsRoot'

/**
 * No-op single kid renderer — segments are rendered by the Root from applogs,
 * not as note3 block children.
 */
const transcriptionsSingleKid = {
	name: 'transcriptions-kid',
	Renderer: () => (
		<span class='text-xs text-neutral-400 italic'>
			Segments are rendered from applog data
		</span>
	),
}

export const pluginTranscriptions: KidRenderingPlugin = {
	type: 'transcriptions',
	displayName: 'Transcriptions',
	Icon: () => <Iconify name='list-checks' scale={120} />,
	Root: TranscriptionsRoot,
	SingleKid: transcriptionsSingleKid,

	/** Inject media entities as virtual roots via Datalog pattern.
	 * media/type (not media/cid) to match the behavior useRoots had hardcoded
	 * before the discovery patterns were actually consumed (seam v1.1). */
	getVirtualRootDiscoveryPatterns: [
		{ en: '?blockID', at: 'media/type' },
	],

	/** Dispatch to this plugin for any entity that has media/cid */
	matchesEntity: (thread: Thread, entityID: EntityID): boolean => {
		try {
			const result = query(thread, [
				{ en: entityID, at: 'media/cid', vl: '?cid' },
			])
			return result.records.length > 0
		} catch {
			return false
		}
	},

	/** Expose settings in the Plugins tab of Settings */
	getSettings: () => ({
		pluginName: 'Transcriptions',
		pluginType: 'transcriptions',
		settings: [
			{
				key: 'showRawApplogs',
				label: 'Show raw applogs for debugging',
				description: 'When enabled, raw transcript applogs from live-tranz are shown in the block extra-props display.',
				type: 'boolean',
				defaultValue: false,
			},
		],
	}),

	/**
	 * Return attribute prefixes to hide from BlockContent extra-props
	 * when hideRawApplogs is enabled. Called inside a createMemo so
	 * the list is re-evaluated on each render.
	 */
	getHiddenExtraPropPrefixes: (): string[] => {
		if (localStorage.getItem('note3.plugins.transcriptions.showRawApplogs') === 'true') return []
		return [
			'annotation/',
			'segment/',
			'sessionMember/',
			'session/',
			'voiceprint/',
		]
	},
}
